import { Select, type SelectProps } from "antd";
import { clsx } from "clsx";

import styles from "./RVTypeaheadSelect.module.scss";

/** A single selectable option for {@link RVTypeaheadSelect}. */
export type RVTypeaheadOption = NonNullable<SelectProps["options"]>[number];

export type RVTypeaheadSelectProps = SelectProps & {
    /** Test id applied to the underlying select input. */
    dataTestid?: string;
};

/**
 * Presentational typeahead (search-as-you-type) select. A thin, schema-agnostic
 * wrapper over antd's `Select`, preconfigured with `showSearch` and `allowClear`.
 *
 * The caller owns the option list and any search behavior: pass `options` and handle
 * `onSearch` to regenerate/filter them. For event-schema-driven usage, use the
 * `useEventSchemaForTypeahead` hook in `ServerData/EventSchema` and spread its result
 * onto this component rather than wiring schema flattening here.
 *
 * @param props - {@link RVTypeaheadSelectProps} (extends antd `SelectProps`).
 * @returns The rendered typeahead select.
 *
 * @example
 * <RVTypeaheadSelect options={options} value={value} onChange={onChange} onSearch={onSearch} />
 */
export function RVTypeaheadSelect({
    dataTestid,
    className,
    suffixIcon,
    value,
    ...selectProps
}: RVTypeaheadSelectProps): JSX.Element {
    return (
        <Select
            allowClear
            showSearch
            {...selectProps}
            value={value ?? undefined}
            suffixIcon={suffixIcon}
            data-testid={dataTestid}
            className={clsx(className, {
                [styles.correctSuffixPositioning]: !!suffixIcon,
            })}
        />
    );
}
